import pino, {} from "pino";
import { redact, redactionEnabled } from "./redact.js";
/**
 * Structured logging on top of pino.
 *
 * pino is the backend rather than a hand-rolled console writer because
 * dd-trace recognises it: with `DD_LOGS_INJECTION=true` (set in each app's
 * platform-config) the tracer patches pino and decorates every line with
 * the active `dd.trace_id` / `dd.span_id`, so log-to-trace correlation needs
 * no code here. See ADR-0021.
 *
 * The wire shape is pinned to what the Datadog pipeline already parses and
 * what call sites emitted before the migration: `{ ts, level, message, ... }`
 * with an ISO timestamp and a string level. The `Logger` interface is
 * unchanged, so the 80+ `createLogger(...)` call sites are untouched.
 *
 * PHI redaction is the single `formatters.log` hook. It runs in-process
 * before anything is serialized or handed to a transport, so JSON and pretty
 * output are masked by the same code path. Trace ids injected by dd-trace are
 * added after this hook and are deliberately not redacted.
 */
function buildOptions() {
    return {
        level: (process.env.LOG_LEVEL ?? "info").toLowerCase(),
        messageKey: "message",
        // Drop pino's default pid/hostname; the prior wire shape carried neither
        // and Datadog already tags pod/host. Keeps records to ts/level/message/ctx.
        base: null,
        // Preserve the prior `ts` ISO timestamp instead of pino's epoch `time`.
        timestamp: () => `,"ts":"${new Date().toISOString()}"`,
        formatters: {
            level: (label) => ({ level: label }),
            log: (obj) => remapReservedStatus(redactionEnabled() ? redact(obj) : obj),
        },
    };
}
/**
 * Datadog's log pipeline runs a Status Remapper that derives each record's
 * severity from a top-level `status` attribute (a Datadog reserved attribute).
 * Call sites legitimately log an operational `status` ("failed_eval",
 * "processed", an HTTP code); Datadog then reinterprets that value as a syslog
 * severity by prefix, so an info line with status "failed_eval" surfaces as
 * EMERGENCY (values starting with "f" map to the top severity). pino already
 * carries the real severity in `level`, so we move any caller `status` to
 * `statusDetail` at this single choke point, leaving `level` as the only
 * severity source. Only the top-level key collides; nested `status` values are
 * not read by the remapper and are left untouched. See ADR-0021.
 */
function remapReservedStatus(obj) {
    if (!Object.hasOwn(obj, "status"))
        return obj;
    const { status, ...rest } = obj;
    // Preserve an explicit statusDetail if a caller already set one.
    return "statusDetail" in rest ? rest : { ...rest, statusDetail: status };
}
// Test seam: when set, JSON logs are written here instead of stdout so a test
// can capture and assert on the emitted line. Production never sets it.
let testDestination;
let rootLogger;
let logContextProvider;
/** @internal Registered by the server-only tenant log context. */
export function setLogContextProvider(provider) {
    logContextProvider = provider;
}
function isBrowserRuntime() {
    const runtime = globalThis;
    return runtime.window !== undefined || runtime.navigator?.product === "ReactNative";
}
export function setLogDestination(destination) {
    testDestination = destination ?? undefined;
    rootLogger = undefined;
}
function root() {
    if (rootLogger)
        return rootLogger;
    const options = buildOptions();
    if (isBrowserRuntime()) {
        rootLogger = pino(options);
        return rootLogger;
    }
    if (process.env.LOG_PRETTY === "true" && !testDestination) {
        // pino-pretty runs in a worker thread. The redacting formatter above
        // still runs in-process first, so pretty output is masked too.
        rootLogger = pino({
            ...options,
            transport: { target: "pino-pretty", options: { messageKey: "message" } },
        });
    }
    else {
        rootLogger = pino(options, testDestination ?? pino.destination({ sync: true }));
    }
    return rootLogger;
}
function makeLogger(bindings) {
    // Bindings are merged into every call's object (rather than pino child
    // bindings) so they pass through the redacting `formatters.log` hook.
    const merge = (context) => ({
        ...bindings,
        ...context,
        ...(logContextProvider?.() ?? {}),
    });
    return {
        debug: (message, context) => root().debug(merge(context), message),
        info: (message, context) => root().info(merge(context), message),
        warn: (message, context) => root().warn(merge(context), message),
        error: (message, context) => root().error(merge(context), message),
        child: (extra) => makeLogger({ ...bindings, ...extra }),
    };
}
export function createLogger(bindings = {}) {
    return makeLogger(bindings);
}
//# sourceMappingURL=logger.js.map